import React, { useState } from "react";
import {
  Box,
  TablePagination,
  Chip,
  CircularProgress,
  Typography,
  Drawer,
  Collapse,
  alpha,
} from "@mui/material";
import {
  Shield as ShieldIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import { useLazyListAuditLogsQuery } from "../../store/auditLogApi";
import { COLORS, TYPO } from "../../styles/uiConstants";
import {
  InfoRow,
  ActionButton,
  FormDatePicker,
  FormSelect,
  FormTextField,
  SearchField,
  DrawerHeader,
  EmptyState,
} from "../../components/atoms";
import { SectionCard } from "../../components/molecules";
import { useClinicTheme } from "../../theme";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Sunucudaki `ActionCategoryEnum` ile BİREBİR aynı olmalıdır
 * (modules/audit/data/models/audit-log.model.js). Eskiden burada sunucuda hiç
 * üretilmeyen "EXPORT" ve "CONFIG" seçenekleri vardı — filtreye konuluyor ama
 * hiçbir zaman sonuç döndürmüyordu; kaldırıldı.
 */
const LOG_CATEGORIES = [
  "CREATE",
  "UPDATE",
  "UPSERT",
  "DELETE",
  "READ",
  "LOGIN",
  "LOGOUT",
  "PASSWORD_CHANGE",
  "PERMISSION_CHANGE",
  "DEVICE_STATUS",
  "DEVICE_OPERATION",
  "SYSTEM_TASK",
  "SECURITY",
  "ERROR",
  "OTHER",
];

const CATEGORY_COLORS = {
  CREATE: "#10b981",
  UPDATE: "#3b82f6",
  UPSERT: "#8b5cf6",
  DELETE: "#ef4444",
  READ: "#94a3b8",
  LOGIN: "#6366f1",
  LOGOUT: "#64748b",
  PASSWORD_CHANGE: "#f59e0b",
  PERMISSION_CHANGE: "#06b6d4",
  DEVICE_STATUS: "#0ea5e9",
  DEVICE_OPERATION: "#0284c7",
  SYSTEM_TASK: "#14b8a6",
  SECURITY: "#b91c1c",
  ERROR: "#dc2626",
  OTHER: "#94a3b8",
};

const CATEGORY_LABELS = {
  CREATE: "Oluşturma",
  UPDATE: "Güncelleme",
  UPSERT: "Oluştur/Güncelle",
  DELETE: "Silme",
  READ: "Okuma",
  LOGIN: "Giriş",
  LOGOUT: "Çıkış",
  PASSWORD_CHANGE: "Şifre Değişikliği",
  PERMISSION_CHANGE: "İzin Değişikliği",
  DEVICE_STATUS: "Cihaz Durumu",
  DEVICE_OPERATION: "Cihaz İşlemi",
  SYSTEM_TASK: "Sistem Görevi",
  SECURITY: "Güvenlik",
  ERROR: "Hata",
  OTHER: "Diğer",
};

const TYPE_LABELS = {
  USER_ACTION: "Kullanıcı İşlemi",
  SYSTEM_AUTO: "Otomatik İşlem",
  IOT_DEVICE: "IoT Cihaz",
  AUTH: "Kimlik Doğrulama",
  API: "API",
};

const RESOURCE_LABELS = {
  User: "Kullanıcı",
  Clinic: "Klinik",
  Appointment: "Randevu",
  Treatment: "Tedavi",
  Examination: "Muayene",
  Device: "Cihaz",
  Medicine: "İlaç",
  TokenPricing: "Token Fiyatlandırma",
  ClinicToken: "Klinik Token",
  PendingUser: "Bekleyen Kullanıcı",
  FoodTemplate: "Besin Şablonu",
  AllergyTemplate: "Alerji Şablonu",
  Disease: "Hastalık",
  DiseaseCategory: "Hastalık Kategorisi",
  LabTestTemplate: "Tahlil Şablonu",
  ImageTemplate: "Görüntü Şablonu",
  ComplaintTemplate: "Şikayet Şablonu",
  Message: "Mesaj",
  TraditionalPractice: "Geleneksel Uygulama",
  PracticeSession: "Uygulama Seansı",
  ConsentTemplate: "Onam Şablonu",
  ConsentRecord: "Onam Kaydı",
};

/**
 * Aksiyon adı → Türkçe etiket. Anahtarlar sunucuda `auditLog("<aksiyon>", ...)`
 * ve `logAuthEvent({ action })` ile üretilen değerlerdir. Yeni bir audit
 * noktası eklenince buraya da bir satır eklenmeli; eksik anahtar ekranda ham
 * İngilizce kod olarak görünür.
 */
const ACTION_LABELS = {
  // ── Kimlik doğrulama / güvenlik (logAuthEvent) ──
  LOGIN_SUCCESS: "Giriş başarılı",
  LOGIN_FAILED: "Giriş başarısız",
  LOGIN_PASSWORD_OK: "Şifre doğrulandı",
  REGISTRATION_SUCCESS: "Kayıt tamamlandı",
  SUSPICIOUS_REQUEST: "Şüpheli istek",

  // ── Token ──
  TOKEN_BILL_CREATED: "Fatura yüklemesi",
  TOKEN_FREEUPLOAD_CREATED: "Bedelsiz token yüklemesi",
  TOKEN_GETRETURN_CREATED: "Token iadesi",
  tokenPricingUpdate: "Token fiyat güncelleme",

  // ── Klinik ──
  createClinic: "Klinik oluşturma",
  updateClinic: "Klinik güncelleme",
  updateClinicInfo: "Klinik bilgi güncelleme",
  updateClinicName: "Klinik adı güncelleme",
  updateClinicContact: "Klinik iletişim güncelleme",
  updateClinicTax: "Klinik vergi güncelleme",
  updateClinicWorkType: "Çalışma şekli güncelleme",
  updateClinicModules: "Klinik modülleri güncelleme",
  updateOwnClinicModules: "Klinik modüllerini güncelleme (klinik)",
  updateClinicWorkingHours: "Çalışma saati güncelleme",
  updateClinicAppearance: "Görünüm güncelleme",
  updateOwnClinicAppearance: "Kendi kliniği görünüm güncelleme",
  uploadClinicLogo: "Logo yükleme",
  uploadOwnClinicLogo: "Kendi kliniği logo yükleme",
  setClinicStatus: "Klinik durum değişikliği",
  hardDeleteClinic: "Klinik kalıcı silme",
  sendClinicEmailChangeCode: "Klinik e-posta değişiklik kodu",
  verifyClinicEmailChange: "Klinik e-posta doğrulama",

  // ── Kullanıcı / personel ──
  createClinicStaff: "Klinik personeli oluşturma",
  updateClinicStaff: "Klinik personeli güncelleme",
  createAdminStaffAccount: "Admin personeli oluşturma",
  updateUser: "Kullanıcı güncelleme",
  deleteUser: "Kullanıcı silme",
  suspendUserAccount: "Hesap askıya alma",
  freezeUserAccount: "Hesap dondurma",
  disableUserAccount: "Hesap devre dışı bırakma",
  activateUserAccount: "Hesap aktifleştirme",
  updateProfile: "Profil güncelleme",

  // ── Hasta ──
  updatePatientBySecretary: "Hasta güncelleme",
  updatePatientAddress: "Hasta adres güncelleme",
  assignPatientToClinic: "Hasta kliniğe atama",

  // ── Bekleyen kullanıcı ──
  approvePendingUser: "Kullanıcı onaylama",
  rejectPendingUser: "Kullanıcı reddetme",
  deletePendingUser: "Bekleyen kullanıcı silme",
  permanentDeletePendingUser: "Bekleyen kullanıcı kalıcı silme",

  // ── Muayene ──
  startExamination: "Muayene başlatma",
  completeExamination: "Muayene tamamlama",
  cancelExamination: "Muayene iptali",

  // ── Sistem değişkenleri: besin ──
  createFoodTemplate: "Besin şablonu oluşturma",
  updateFoodTemplate: "Besin şablonu güncelleme",
  setFoodTemplateStatus: "Besin şablonu durum değişikliği",
  bulkUpsertFoodTemplates: "Toplu besin şablonu güncelleme",

  // ── Sistem değişkenleri: alerji ──
  createAllergyTemplate: "Alerji şablonu oluşturma",
  updateAllergyTemplate: "Alerji şablonu güncelleme",
  setAllergyTemplateStatus: "Alerji şablonu durum değişikliği",
  bulkUpsertAllergyTemplates: "Toplu alerji şablonu güncelleme",

  // ── Geleneksel uygulamalar (seans, onam, katalog) ──
  createTraditionalPractice: "Geleneksel uygulama türü oluşturma",
  updateTraditionalPractice: "Geleneksel uygulama türü güncelleme",
  setTraditionalPracticeStatus: "Geleneksel uygulama türü durum değişikliği",
  deleteTraditionalPractice: "Geleneksel uygulama türü silme",
  readPatientPracticeSessions: "Hasta uygulama seanslarını görüntüleme",
  createPracticeSession: "Uygulama seansı kaydetme",
  updatePracticeSession: "Uygulama seansı güncelleme",
  createConsentTemplate: "Onam şablonu oluşturma",
  updateConsentTemplate: "Onam şablonu güncelleme",
  deleteConsentTemplate: "Onam şablonu silme",
  readPatientConsents: "Hasta onamlarını görüntüleme",
  createConsentRecord: "Onam kaydetme",
  updateConsentSettings: "Onam ayarı değişikliği",

  // ── Sistem değişkenleri: tanı ──
  createDiagnosisCategory: "Tanı kategorisi oluşturma",
  updateDiagnosisCategory: "Tanı kategorisi güncelleme",
  setDiagnosisCategoryStatus: "Tanı kategorisi durum değişikliği",
  createDiagnosis: "Tanı oluşturma",
  updateDiagnosis: "Tanı güncelleme",
  setDiagnosisStatus: "Tanı durum değişikliği",
  bulkUpsertDiagnoses: "Toplu tanı güncelleme",

  // ── Sistem değişkenleri: tahlil ──
  createLabTestTemplate: "Tahlil şablonu oluşturma",
  updateLabTestTemplate: "Tahlil şablonu güncelleme",
  setLabTestTemplateStatus: "Tahlil şablonu durum değişikliği",
  bulkUpsertLabTestTemplates: "Toplu tahlil şablonu güncelleme",

  // ── Sistem değişkenleri: ilaç ──
  createMedicineTemplate: "İlaç şablonu oluşturma",
  updateMedicineTemplate: "İlaç şablonu güncelleme",
  setMedicineTemplateStatus: "İlaç şablonu durum değişikliği",
  bulkUpsertMedicineTemplates: "Toplu ilaç şablonu güncelleme",

  // ── Sistem değişkenleri: görüntü ──
  createImageTemplate: "Görüntü şablonu oluşturma",
  updateImageTemplate: "Görüntü şablonu güncelleme",
  setImageTemplateStatus: "Görüntü şablonu durum değişikliği",
  bulkUpsertImageTemplates: "Toplu görüntü şablonu güncelleme",

  // ── Sistem değişkenleri: şikayet ──
  createComplaintTemplate: "Şikayet şablonu oluşturma",
  updateComplaintTemplate: "Şikayet şablonu güncelleme",
  setComplaintTemplateStatus: "Şikayet şablonu durum değişikliği",
  bulkUpsertComplaintTemplates: "Toplu şikayet şablonu güncelleme",

  // ── Cihaz (ortak cihaz kaydı) ──
  provisionDevice: "Cihaz sisteme tanıtma",
  bindDeviceToPatient: "Cihaz hastaya bağlama",
  unbindDeviceFromPatient: "Cihaz bağını çözme",
  assignDeviceToPatient: "Cihaz hastaya atama (panel)",
  assignDeviceToService: "Cihaz servise alma",
  assignDeviceToDepot: "Cihaz depoya alma",
  deleteDevice: "Cihaz silme",

  // ── Mesajlaşma ──
  sendMessage: "Mesaj gönderme",
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Filtre dropdown seçenekleri (FormSelect `{ value, label }`)
const TYPE_OPTIONS = ["USER_ACTION", "SYSTEM_AUTO", "IOT_DEVICE", "AUTH", "API"].map((t) => ({
  value: t,
  label: TYPE_LABELS[t] || t,
}));
const CATEGORY_OPTIONS = LOG_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] || c }));
const SUCCESS_OPTIONS = [
  { value: "true", label: "Başarılı" },
  { value: "false", label: "Başarısız" },
];

function timeAgo(date) {
  const now = new Date();
  const d = new Date(date);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "Az once";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk once`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} saat once`;
  return `${Math.floor(diff / 86400)} gun once`;
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function getCategoryColor(category) {
  return CATEGORY_COLORS[category] || COLORS.slate400;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AuditLogs() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({ startDate: yesterday, endDate: today, type: "", category: "", success: "", action: "", userEmail: "", resourceType: "", clinicId: "" });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [detailLog, setDetailLog] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [fetchLogs, { data, isLoading, error }] = useLazyListAuditLogsQuery();

  const rawLogs = data?.data?.logs || data?.logs;
  const logs = Array.isArray(rawLogs) ? rawLogs : [];
  const totalCount = data?.data?.total || data?.total || data?.data?.pagination?.total || logs.length;

  const doSearch = (overridePage) => {
    if (!token) return;
    const p = overridePage !== undefined ? overridePage : page;
    fetchLogs({
      token,
      page: p + 1,
      limit,
      ...(filters.startDate && { startDate: filters.startDate }),
      ...(filters.endDate && { endDate: filters.endDate }),
      ...(filters.type && { type: filters.type }),
      ...(filters.category && { category: filters.category }),
      ...(filters.action && { action: filters.action }),
      ...(filters.userEmail && { userEmail: filters.userEmail }),
      ...(filters.resourceType && { resourceType: filters.resourceType }),
      ...(filters.clinicId && { clinicId: filters.clinicId }),
      ...(filters.success !== "" && { success: filters.success === "true" }),
    });
    setHasSearched(true);
  };

  const handleSearch = () => { setPage(0); doSearch(0); };
  const handlePageChange = (_, p) => { setPage(p); doSearch(p); };
  const handleLimitChange = (e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); doSearch(0); };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const resetFilters = () => {
    setFilters({ startDate: yesterday, endDate: today, type: "", category: "", success: "", action: "", userEmail: "", resourceType: "", clinicId: "" });
    setPage(0);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Sunucuda gerçekten üretilen resourceType değerleri (auditLog 3. argümanı).
  const RESOURCE_TYPES = [
    "User", "Clinic", "Device", "Examination", "Medicine", "ClinicToken",
    "TokenPricing", "PendingUser", "FoodTemplate", "AllergyTemplate",
    "Disease", "DiseaseCategory", "LabTestTemplate", "ImageTemplate",
    "ComplaintTemplate",
  ];
  const RESOURCE_OPTIONS = RESOURCE_TYPES.map((r) => ({ value: r, label: RESOURCE_LABELS[r] || r }));

  const hasActiveFilters = filters.type || filters.category || filters.success || filters.action || filters.userEmail || filters.resourceType || filters.clinicId;

  const filterContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Satır 1: Tarih + Tip + Kategori + Sonuç */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <FormDatePicker label="Başlangıç" value={filters.startDate} onChange={(v) => handleFilterChange("startDate", v)} maxDate={filters.endDate || undefined} sx={{ width: 160 }} />
        <FormDatePicker label="Bitiş" value={filters.endDate} onChange={(v) => handleFilterChange("endDate", v)} minDate={filters.startDate || undefined} sx={{ width: 160 }} />
        <FormSelect
          palette={palette}
          value={filters.type}
          onChange={(v) => handleFilterChange("type", v)}
          options={TYPE_OPTIONS}
          placeholder="Tüm Tipler"
          SelectProps={{ displayEmpty: true }}
          fullWidth={false}
          sx={{ minWidth: 140 }}
        />
        <FormSelect
          palette={palette}
          value={filters.category}
          onChange={(v) => handleFilterChange("category", v)}
          options={CATEGORY_OPTIONS}
          placeholder="Tüm Kategoriler"
          SelectProps={{ displayEmpty: true }}
          fullWidth={false}
          sx={{ minWidth: 150 }}
        />
        <FormSelect
          palette={palette}
          value={filters.success}
          onChange={(v) => handleFilterChange("success", v)}
          options={SUCCESS_OPTIONS}
          placeholder="Tüm Sonuçlar"
          SelectProps={{ displayEmpty: true }}
          fullWidth={false}
          sx={{ minWidth: 120 }}
        />
      </Box>

      {/* Satır 2: Aksiyon + Kullanıcı + Kaynak Tipi + Ara/Sıfırla */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <SearchField placeholder="Aksiyon ara..." value={filters.action} onChange={(v) => handleFilterChange("action", v)} sx={{ width: 180 }} />
        <FormTextField palette={palette} placeholder="Kullanıcı e-posta..." value={filters.userEmail} onChange={(e) => handleFilterChange("userEmail", e.target.value)} inputProps={{ "aria-label": "Kullanıcı e-posta" }} sx={{ width: 180 }} />
        <FormSelect
          palette={palette}
          value={filters.resourceType}
          onChange={(v) => handleFilterChange("resourceType", v)}
          options={RESOURCE_OPTIONS}
          placeholder="Tüm Kaynaklar"
          SelectProps={{ displayEmpty: true }}
          fullWidth={false}
          sx={{ minWidth: 140 }}
        />

        <Box sx={{ flex: 1 }} />

        {hasActiveFilters && (
          <Typography component="button" onClick={resetFilters}
            sx={{ background: "none", border: "none", cursor: "pointer", color: COLORS.slate400, fontSize: "0.75rem", fontWeight: 600, textDecoration: "underline", "&:hover": { color: COLORS.slate600 } }}>
            Sıfırla
          </Typography>
        )}
        <ActionButton variant="save" compact palette={palette} onClick={handleSearch} loading={isLoading} label="Ara" />
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: COLORS.slate50,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Filtreler */}
      <SectionCard title="Filtreler">
        <Box sx={{ px: 2.5, py: 1.5 }}>
          {filterContent}
        </Box>
      </SectionCard>

      {/* Log listesi */}
      <SectionCard
        title={`Denetim Günlüğü${totalCount ? ` (${totalCount})` : ""}`}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        {/* Error */}
        {error && (
          <Box
            sx={{
              mx: 2.5,
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: COLORS.errorLightest,
              border: `1px solid ${COLORS.errorLight}`,
              color: COLORS.errorDark,
              fontSize: "0.85rem",
            }}
          >
            Denetim günlüğü yüklenirken hata oluştu.
          </Box>
        )}

        {/* Loading */}
        {isLoading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress size={20} sx={{ color: COLORS.slate300 }} />
          </Box>
        ) : logs.length === 0 ? (
          /* Empty state */
          <EmptyState
            variant="inline"
            icon={<ShieldIcon />}
            title={hasSearched ? "Kayıt bulunamadı" : "Filtreleri seçip 'Ara' butonuna tıklayın"}
            sx={{ flex: 1, py: 8 }}
          />
        ) : (
          <>
          {/* Table header */}
          <Box sx={{
            display: "grid", gridTemplateColumns: "90px 1fr 180px 100px 90px 70px",
            alignItems: "center", px: 2.5, py: 1,
            borderBottom: `1px solid ${COLORS.slate200}`, bgcolor: COLORS.slate50, flexShrink: 0, gap: 1,
          }}>
            {["Kategori", "Aksiyon", "Kullanıcı", "Kaynak", "Zaman", "Sonuç"].map((h) => (
              <Typography key={h} sx={{ fontSize: "0.65rem", fontWeight: 800, color: COLORS.slate400, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</Typography>
            ))}
          </Box>
          {/* Log rows */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {logs.map((log, idx) => (
              <LogRow
                key={log._id || idx}
                log={log}
                isLast={idx === logs.length - 1}
                onClick={() => setDetailLog(log)}
              />
            ))}
          </Box>
          </>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleLimitChange}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Sayfa başına:"
          sx={{ borderTop: `1px solid ${COLORS.slate200}`, flexShrink: 0 }}
        />
      </SectionCard>

      {/* Detail drawer */}
      <LogDetailDrawer log={detailLog} onClose={() => setDetailLog(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LogRow — card-style row
// ---------------------------------------------------------------------------

function LogRow({ log, isLast, onClick }) {
  const catColor = getCategoryColor(log.category);
  const isSuccess = log.success;
  const userEmail = log.userEmail || log.user?.email || "-";
  const resourceType = RESOURCE_LABELS[log.resourceType] || log.resourceType || "-";
  const timestamp = log.timestamp || log.createdAt;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "grid",
        gridTemplateColumns: "90px 1fr 180px 100px 90px 70px",
        alignItems: "center",
        gap: 1,
        px: 2.5,
        py: 1.25,
        cursor: "pointer",
        borderBottom: isLast ? "none" : `1px solid ${COLORS.slate100}`,
        "&:hover": { bgcolor: alpha(COLORS.slate50, 0.8) },
        transition: "background-color 0.15s",
      }}
    >
      {/* Kategori */}
      <Chip
        label={CATEGORY_LABELS[log.category] || log.category || "-"}
        size="small"
        sx={{ fontWeight: 700, fontSize: "0.6rem", height: 22, borderRadius: 1, bgcolor: alpha(catColor, 0.12), color: catColor, width: "fit-content" }}
      />
      {/* Aksiyon */}
      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: COLORS.slate800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {ACTION_LABELS[log.action] || log.action || "-"}
      </Typography>
      {/* Kullanıcı */}
      <Typography sx={{ fontSize: "0.75rem", color: COLORS.slate500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {userEmail}
      </Typography>
      {/* Kaynak */}
      <Typography sx={{ fontSize: "0.75rem", color: COLORS.slate500 }}>
        {resourceType}
      </Typography>
      {/* Zaman */}
      <Typography sx={{ fontSize: "0.7rem", color: COLORS.slate400, whiteSpace: "nowrap" }}>
        {timestamp ? timeAgo(timestamp) : "-"}
      </Typography>
      {/* Sonuç */}
      <Chip
        label={isSuccess ? "Başarılı" : "Başarısız"}
        size="small"
        sx={{ fontWeight: 700, fontSize: "0.6rem", height: 20, borderRadius: 1, bgcolor: isSuccess ? COLORS.successLight : COLORS.errorLight, color: isSuccess ? COLORS.successDarker : COLORS.errorDark, width: "fit-content" }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LogDetailDrawer
// ---------------------------------------------------------------------------

function LogDetailDrawer({ log, onClose }) {
  const open = Boolean(log);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 640 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {log && (
        <>
          <DrawerHeader
            icon={<ShieldIcon />}
            title="Log Detayı"
            subtitle={ACTION_LABELS[log.action] || log.action || "-"}
            onClose={onClose}
          />

          {/* Body */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <InfoRow label="Zaman">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {formatDate(log.timestamp || log.createdAt)}
              </Typography>
            </InfoRow>

            <InfoRow label="Aksiyon">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, fontWeight: 600, pt: 0.4 }}>
                {ACTION_LABELS[log.action] || log.action || "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="Kategori">
              <Chip
                label={CATEGORY_LABELS[log.category] || log.category || "-"}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: TYPO.caption,
                  height: 24,
                  borderRadius: 1,
                  bgcolor: alpha(getCategoryColor(log.category), 0.12),
                  color: getCategoryColor(log.category),
                  mt: 0.2,
                }}
              />
            </InfoRow>

            <InfoRow label="Tip">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {TYPE_LABELS[log.type] || log.type || "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="Kullanıcı">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {log.userEmail || log.user?.email || "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="Rol">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {log.userRole || log.user?.role || "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="IP Adresi">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {log.ipAddress || log.ip || "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="Endpoint">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {log.endpoint || log.method
                  ? `${log.method || ""} ${log.endpoint || ""}`.trim()
                  : "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="Durum">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 0.2 }}>
                {log.statusCode && (
                  <Typography
                    sx={{ fontSize: TYPO.body, color: COLORS.slate800 }}
                  >
                    {log.statusCode}
                  </Typography>
                )}
                <Chip
                  label={log.success ? "Başarılı" : "Başarısız"}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: TYPO.caption,
                    height: 22,
                    borderRadius: 1,
                    bgcolor: log.success
                      ? COLORS.successLight
                      : COLORS.errorLight,
                    color: log.success
                      ? COLORS.successDarker
                      : COLORS.errorDark,
                  }}
                />
              </Box>
            </InfoRow>

            <InfoRow label="Süre">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {log.duration ? `${log.duration}ms` : "-"}
              </Typography>
            </InfoRow>

            <InfoRow label="Kaynak">
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 }}>
                {log.resourceType || log.resourceId
                  ? `${RESOURCE_LABELS[log.resourceType] || log.resourceType || ""} ${log.resourceId ? "— " + log.resourceId : ""}`.trim()
                  : "-"}
              </Typography>
            </InfoRow>

            {/* Hata detayı — sadece başarısız isteklerde */}
            {!log.success && log.error && (
              <InfoRow label="Hata Nedeni" noBorder>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(COLORS.error, 0.06), border: `1px solid ${alpha(COLORS.error, 0.12)}` }}>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.error }}>{log.error.message || "Bilinmeyen hata"}</Typography>
                  {log.error.code && <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mt: 0.25 }}>Kod: {log.error.code}</Typography>}
                  {log.error.statusCode && <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>HTTP: {log.error.statusCode}</Typography>}
                </Box>
              </InfoRow>
            )}

            {/* Collapsible JSON sections */}
            <CollapsibleJson title="Değişiklikler" data={log.changes} />
            <CollapsibleJson title="Eski Değerler" data={log.oldValues} />
            <CollapsibleJson title="Yeni Değerler" data={log.newValues} />
            <CollapsibleJson title="Metadata" data={log.metadata} />
            <CollapsibleJson title="İstek Gövdesi" data={log.requestBody} />
            {!log.success && <CollapsibleJson title="Hata Detayı" data={log.error} />}
          </Box>
        </>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleJson — expandable JSON block
// ---------------------------------------------------------------------------

function CollapsibleJson({ title, data }) {
  const [open, setOpen] = useState(false);

  if (!data || (typeof data === "object" && Object.keys(data).length === 0))
    return null;

  return (
    <Box sx={{ borderTop: `1px solid ${COLORS.slate100}` }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.25,
          cursor: "pointer",
          "&:hover": { bgcolor: alpha(COLORS.slate50, 0.8) },
        }}
      >
        <Typography
          sx={{
            fontSize: TYPO.sectionTitle,
            fontWeight: 700,
            color: COLORS.slate600,
          }}
        >
          {title}
        </Typography>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 18, color: COLORS.slate400 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18, color: COLORS.slate400 }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box
          sx={{
            mx: 2.5,
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: COLORS.slate50,
            border: `1px solid ${COLORS.slate200}`,
            overflow: "auto",
            maxHeight: 240,
          }}
        >
          <Typography
            component="pre"
            sx={{
              fontSize: TYPO.caption,
              color: COLORS.slate700,
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              m: 0,
            }}
          >
            {JSON.stringify(data, null, 2)}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
